/**
 * Title Cell Renderer
 *
 * Renders the vulnerability title/name with optional description (DNS).
 * Used in the vulnerability name column of the vulnerabilities table.
 */

import React from 'react';

import { NameColumn } from '@/components/NameColumn';
import type { RiskWithVulnerability } from '@/hooks/useRisks';

interface TitleCellProps {
  risk: RiskWithVulnerability;
}

/**
 * Memoized cell renderer for vulnerability title column to improve performance
 *
 * Displays the vulnerability title (or falls back to name) with DNS as description.
 * Clicking the cell opens the vulnerability drawer for detailed information.
 *
 * @param risk - The vulnerability/risk to render
 * @returns NameColumn component with title and description
 */
export const TitleCell: React.FC<TitleCellProps> = React.memo(({ risk }) => {
  // Prefer title over name for display
  const displayName = risk?.title || risk.name;
  const description = risk.dns;

  return <NameColumn name={displayName} description={description} />;
});

TitleCell.displayName = 'TitleCell';
